import React, { useState, useEffect, useRef } from 'react';
import supabase from '../../supabase/supabase';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiEye, FiImage, FiArrowLeft, FiX, FiCopy, FiAlignLeft, FiAlignCenter, FiAlignRight, FiAlignJustify } from 'react-icons/fi';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const editorRef = useRef(null);
  const [newImageUrl, setNewImageUrl] = useState('');

  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    gallery_images: [],
    author: 'Ceylanthro Team',
    published: false
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const { data } = await supabase
      .from('blog_posts')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setPosts(data);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setForm(f => ({
      ...f,
      title: newTitle,
      slug: editing && currentPost?.slug ? f.slug : generateSlug(newTitle)
    }));
  };

  const handleNewPost = () => {
    setCurrentPost(null);
    setForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      cover_image: '',
      gallery_images: [],
      author: 'Ceylanthro Team',
      published: false
    });
    setEditing(true);
    setSaved(false);
  };

  const handleEditPost = (post) => {
    setCurrentPost(post);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt || '',
      content: post.content || '',
      cover_image: post.cover_image || '',
      gallery_images: post.gallery_images || [],
      author: post.author || 'Ceylanthro Team',
      published: post.published || false
    });
    setEditing(true);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) return;
    setSaving(true);

    try {
      if (currentPost) {
        const { error } = await supabase
          .from('blog_posts')
          .update({
            ...form,
            published_at: form.published && !currentPost.published ? new Date().toISOString() : currentPost.published_at,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentPost.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([{
            ...form,
            published_at: form.published ? new Date().toISOString() : null
          }]);
        if (error) throw error;
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      setEditing(false);
      fetchPosts();
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    const { error } = await supabase.from('blog_posts').delete().eq('id', id);
    if (!error) fetchPosts();
  };

  const addGalleryImage = () => {
    if (!newImageUrl.trim()) return;
    setForm(f => ({ ...f, gallery_images: [...f.gallery_images, newImageUrl.trim()] }));
    setNewImageUrl('');
  };

  const removeGalleryImage = (index) => {
    setForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, i) => i !== index) }));
  };

  const insertGalleryImage = (url) => {
    const textarea = editorRef.current;
    if (!textarea) {
      setForm(f => ({
        ...f,
        content: f.content + `\n<img src="${url}" alt="Blog image" style="width:100%;border-radius:12px;margin:20px 0;" />`
      }));
      return;
    }
    const start = textarea.selectionStart;
    const imgTag = `<img src="${url}" alt="Blog image" style="width:100%;border-radius:12px;margin:20px 0;" />`;
    const newContent = form.content.substring(0, start) + imgTag + form.content.substring(start);
    setForm(f => ({ ...f, content: newContent }));
  };

  const insertAtCursor = (insertion) => {
    const textarea = editorRef.current;
    if (!textarea) {
      setForm(f => ({ ...f, content: f.content + insertion }));
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = form.content.substring(0, start) + insertion + form.content.substring(end);
    setForm(f => ({ ...f, content: newContent }));
  };

  const wrapSelection = (before, after) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = form.content.substring(start, end);
    const wrapped = before + (selected || 'text') + after;
    const newContent = form.content.substring(0, start) + wrapped + form.content.substring(end);
    setForm(f => ({ ...f, content: newContent }));
  };

  const insertTag = (tag) => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = form.content.substring(start, end);
    let insertion = '';

    switch (tag) {
      case 'h1': insertion = `<h1>${selected || 'Main Heading'}</h1>`; break;
      case 'h2': insertion = `<h2>${selected || 'Heading'}</h2>`; break;
      case 'h3': insertion = `<h3>${selected || 'Subheading'}</h3>`; break;
      case 'h4': insertion = `<h4>${selected || 'Small Heading'}</h4>`; break;
      case 'p': insertion = `<p>${selected || 'Paragraph text'}</p>`; break;
      case 'ul': insertion = `<ul>\n  <li>${selected || 'List item'}</li>\n  <li>Another item</li>\n</ul>`; break;
      case 'ol': insertion = `<ol>\n  <li>${selected || 'First item'}</li>\n  <li>Second item</li>\n</ol>`; break;
      case 'strong': wrapSelection('<strong>', '</strong>'); return;
      case 'em': wrapSelection('<em>', '</em>'); return;
      case 'u': wrapSelection('<u>', '</u>'); return;
      case 's': wrapSelection('<s>', '</s>'); return;
      case 'sup': wrapSelection('<sup>', '</sup>'); return;
      case 'sub': wrapSelection('<sub>', '</sub>'); return;
      case 'a': insertion = `<a href="https://">${selected || 'Link text'}</a>`; break;
      case 'img': insertion = `<img src="${selected || 'https://'}" alt="Blog image" style="width:100%;border-radius:12px;margin:20px 0;" />`; break;
      case 'img-center': insertion = `<div style="text-align:center;"><img src="${selected || 'https://'}" alt="Blog image" style="max-width:80%;border-radius:12px;margin:20px auto;" /></div>`; break;
      case 'img-small': insertion = `<div style="text-align:center;"><img src="${selected || 'https://'}" alt="Blog image" style="max-width:50%;border-radius:12px;margin:20px auto;" /></div>`; break;
      case 'blockquote': insertion = `<blockquote>${selected || 'Quote text'}</blockquote>`; break;
      case 'br': insertion = '<br />'; break;
      case 'hr': insertion = '<hr />'; break;
      case 'div': insertion = `<div>${selected || 'Content block'}</div>`; break;
      case 'align-left': wrapSelection('<div style="text-align:left;">', '</div>'); return;
      case 'align-center': wrapSelection('<div style="text-align:center;">', '</div>'); return;
      case 'align-right': wrapSelection('<div style="text-align:right;">', '</div>'); return;
      case 'align-justify': wrapSelection('<div style="text-align:justify;">', '</div>'); return;
      case 'highlight': wrapSelection('<mark style="background:#fef3c7;padding:2px 6px;border-radius:4px;">', '</mark>'); return;
      case 'small': wrapSelection('<small>', '</small>'); return;
      case 'large': wrapSelection('<span style="font-size:1.2em;">', '</span>'); return;
      case 'color-gold': wrapSelection('<span style="color:#d4af37;">', '</span>'); return;
      case 'color-sage': wrapSelection('<span style="color:#2c3625;">', '</span>'); return;
      case 'color-red': wrapSelection('<span style="color:#ef4444;">', '</span>'); return;
      case 'callout': insertion = `<div style="background:#faf9f6;border-left:4px solid #d4af37;padding:20px 24px;margin:24px 0;border-radius:0 12px 12px 0;"><p style="margin:0;color:#4b5563;">${selected || 'Important note or callout text'}</p></div>`; break;
      case 'info-box': insertion = `<div style="background:#eff6ff;border:1px solid #bfdbfe;padding:20px 24px;margin:24px 0;border-radius:12px;"><p style="margin:0 0 4px 0;font-weight:bold;color:#1e40af;">ℹ️ Info</p><p style="margin:0;color:#1e3a5f;">${selected || 'Informational content'}</p></div>`; break;
      case 'warning-box': insertion = `<div style="background:#fef3c7;border:1px solid #fcd34d;padding:20px 24px;margin:24px 0;border-radius:12px;"><p style="margin:0 0 4px 0;font-weight:bold;color:#92400e;">⚠️ Note</p><p style="margin:0;color:#78350f;">${selected || 'Warning or important note'}</p></div>`; break;
      case 'two-col': insertion = `<div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin:24px 0;"><div>${selected || 'Left column content'}</div><div>Right column content</div></div>`; break;
      case 'spacer': insertion = '<div style="height:32px;"></div>'; break;
      default: break;
    }

    insertAtCursor(insertion);
  };

  if (editing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setEditing(false)}
            className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-brand-darkSage transition-colors"
          >
            <FiArrowLeft size={16} /> Back to Posts
          </button>
          <div className="flex items-center gap-3">
            {saved && <span className="text-sm font-medium text-green-600 animate-pulse">✓ Saved</span>}
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-brand-darkSage text-white px-6 py-2.5 rounded-xl text-sm font-bold uppercase tracking-widest hover:bg-brand-gold transition-all disabled:opacity-50 shadow-lg"
            >
              <FiSave size={14} />
              {saving ? 'Saving...' : 'Save Post'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Title & Slug */}
          <div className="p-6 border-b border-gray-100 space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={handleTitleChange}
                className="w-full text-2xl font-serif font-bold text-gray-800 border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none"
                placeholder="Enter blog post title..."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">URL Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm(f => ({ ...f, slug: e.target.value }))}
                  className="w-full text-sm font-mono border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none"
                  placeholder="url-friendly-slug"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Author</label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => setForm(f => ({ ...f, author: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none"
                />
              </div>
            </div>
          </div>

          {/* Cover Image & Excerpt */}
          <div className="p-6 border-b border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                <FiImage className="inline mr-1" size={12} /> Cover Image URL
              </label>
              <input
                type="url"
                value={form.cover_image}
                onChange={(e) => setForm(f => ({ ...f, cover_image: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none"
                placeholder="https://..."
              />
              {form.cover_image && (
                <img src={form.cover_image} alt="Cover" className="mt-2 h-20 w-full object-cover rounded-lg border border-gray-100" />
              )}
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Excerpt (Short Summary)</label>
              <textarea
                value={form.excerpt}
                onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))}
                rows={3}
                className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none resize-none"
                placeholder="Brief summary for blog listing..."
              />
            </div>
          </div>

          {/* Gallery Images */}
          <div className="p-6 border-b border-gray-100 bg-gray-50/30">
            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
              <FiImage className="inline mr-1" size={12} /> Image Gallery (insert into content)
            </label>
            <p className="text-xs text-gray-400 mb-3">Add image URLs to your gallery, then click the copy icon to insert them at cursor position.</p>

            <div className="flex gap-2 mb-4">
              <input
                type="url"
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGalleryImage(); } }}
                className="flex-1 text-sm border border-gray-200 rounded-xl px-4 py-2.5 focus:border-brand-gold outline-none"
                placeholder="Paste image URL and press Enter or click Add..."
              />
              <button
                type="button"
                onClick={addGalleryImage}
                className="px-4 py-2.5 bg-brand-darkSage text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all flex items-center gap-1"
              >
                <FiPlus size={14} /> Add
              </button>
            </div>

            {form.gallery_images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {form.gallery_images.map((url, index) => (
                  <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 bg-white">
                    <img src={url} alt={`Gallery ${index + 1}`} className="w-full h-24 object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button onClick={() => insertGalleryImage(url)} className="p-2 bg-white rounded-lg text-brand-darkSage hover:bg-brand-gold hover:text-white transition-colors" title="Insert at cursor">
                        <FiCopy size={14} />
                      </button>
                      <button onClick={() => removeGalleryImage(index)} className="p-2 bg-white rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors" title="Remove from gallery">
                        <FiX size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {form.gallery_images.length === 0 && (
              <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-xl">
                <FiImage className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-400">No gallery images yet. Add image URLs above.</p>
              </div>
            )}
          </div>

          {/* Publish Toggle - Large & Prominent */}
          <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-widest transition-all duration-300 ${
                form.published
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                <span className={`w-2.5 h-2.5 rounded-full ${form.published ? 'bg-green-500' : 'bg-amber-500'} animate-pulse`} />
                {form.published ? 'Published' : 'Draft'}
              </div>
              <span className="text-sm text-gray-500">
                {form.published ? 'This post is live and visible to the public' : 'This post is saved as a draft and not visible'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, published: !f.published }))}
              className={`relative inline-flex h-10 w-20 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-4 ${
                form.published
                  ? 'bg-green-500 focus:ring-green-200'
                  : 'bg-gray-300 focus:ring-gray-100'
              }`}
            >
              <span
                className={`inline-block h-8 w-8 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
                  form.published ? 'translate-x-11' : 'translate-x-1'
                }`}
              />
              <span className={`absolute text-[10px] font-bold uppercase tracking-wider ${
                form.published ? 'left-2.5 text-white' : 'right-2.5 text-gray-500'
              }`}>
                {form.published ? 'On' : 'Off'}
              </span>
            </button>
          </div>

          {/* Enhanced Toolbar */}
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 space-y-2">
            {/* Row 1: Structure */}
            <div className="flex flex-wrap gap-1 items-center">
              <ToolbarLabel text="Structure" />
              <ToolbarBtn label="H1" onClick={() => insertTag('h1')} />
              <ToolbarBtn label="H2" onClick={() => insertTag('h2')} />
              <ToolbarBtn label="H3" onClick={() => insertTag('h3')} />
              <ToolbarBtn label="H4" onClick={() => insertTag('h4')} />
              <ToolbarBtn label="¶ Para" onClick={() => insertTag('p')} />
              <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
              <ToolbarBtn label="• List" onClick={() => insertTag('ul')} />
              <ToolbarBtn label="1. List" onClick={() => insertTag('ol')} />
              <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
              <ToolbarBtn label="Quote" onClick={() => insertTag('blockquote')} />
              <ToolbarBtn label="Link" onClick={() => insertTag('a')} />
              <ToolbarBtn label="Line" onClick={() => insertTag('hr')} />
              <ToolbarBtn label="Break" onClick={() => insertTag('br')} />
              <ToolbarBtn label="Spacer" onClick={() => insertTag('spacer')} />
            </div>

            {/* Row 2: Text Formatting */}
            <div className="flex flex-wrap gap-1 items-center">
              <ToolbarLabel text="Text" />
              <ToolbarBtn label="B" onClick={() => insertTag('strong')} bold />
              <ToolbarBtn label="I" onClick={() => insertTag('em')} italic />
              <ToolbarBtn label="U" onClick={() => insertTag('u')} underline />
              <ToolbarBtn label="S" onClick={() => insertTag('s')} strike />
              <ToolbarBtn label="Small" onClick={() => insertTag('small')} />
              <ToolbarBtn label="Large" onClick={() => insertTag('large')} />
              <ToolbarBtn label="Highlight" onClick={() => insertTag('highlight')} />
              <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
              <ToolbarLabel text="Color" />
              <ToolbarBtn label="Gold" onClick={() => insertTag('color-gold')} color="#d4af37" />
              <ToolbarBtn label="Sage" onClick={() => insertTag('color-sage')} color="#2c3625" />
              <ToolbarBtn label="Red" onClick={() => insertTag('color-red')} color="#ef4444" />
            </div>

            {/* Row 3: Alignment */}
            <div className="flex flex-wrap gap-1 items-center">
              <ToolbarLabel text="Align" />
              <ToolbarBtn icon={<FiAlignLeft size={13} />} onClick={() => insertTag('align-left')} title="Align Left" />
              <ToolbarBtn icon={<FiAlignCenter size={13} />} onClick={() => insertTag('align-center')} title="Center" />
              <ToolbarBtn icon={<FiAlignRight size={13} />} onClick={() => insertTag('align-right')} title="Align Right" />
              <ToolbarBtn icon={<FiAlignJustify size={13} />} onClick={() => insertTag('align-justify')} title="Justify" />
              <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
              <ToolbarLabel text="Images" />
              <ToolbarBtn label="Full Image" onClick={() => insertTag('img')} />
              <ToolbarBtn label="Center Image" onClick={() => insertTag('img-center')} />
              <ToolbarBtn label="Small Image" onClick={() => insertTag('img-small')} />
            </div>

            {/* Row 4: Special Blocks */}
            <div className="flex flex-wrap gap-1 items-center">
              <ToolbarLabel text="Blocks" />
              <ToolbarBtn label="💡 Callout" onClick={() => insertTag('callout')} />
              <ToolbarBtn label="ℹ️ Info Box" onClick={() => insertTag('info-box')} />
              <ToolbarBtn label="⚠️ Warning" onClick={() => insertTag('warning-box')} />
              <ToolbarBtn label="▦ Two Columns" onClick={() => insertTag('two-col')} />
              <ToolbarBtn label="Div" onClick={() => insertTag('div')} />
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Content (HTML)</label>
            <textarea
              ref={editorRef}
              value={form.content}
              onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
              rows={22}
              className="w-full font-mono text-sm border border-gray-200 rounded-xl px-4 py-3 focus:border-brand-gold outline-none resize-y leading-relaxed"
              placeholder="Write your blog post content in HTML. Use the toolbar above to insert formatted elements..."
            />
            <p className="text-xs text-gray-400 mt-2">
              <strong>Tip:</strong> Select text then click a formatting button (B, I, U, Highlight, Color) to wrap the selection. Use Structure buttons to insert new elements.
            </p>
          </div>

          {/* Preview */}
          <div className="p-6 border-t border-gray-100">
            <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Preview</label>
            <div
              className="blog-content border border-gray-100 rounded-xl p-6 bg-gray-50/30 min-h-[200px]"
              dangerouslySetInnerHTML={{ __html: form.content }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold text-gray-800">Blog Posts</h2>
          <p className="text-sm text-gray-500 mt-1">Create and manage blog articles for your store.</p>
        </div>
        <button
          onClick={handleNewPost}
          className="flex items-center gap-2 bg-brand-darkSage text-white px-6 py-3 rounded-xl text-sm font-bold uppercase tracking-widest hover:bg-brand-gold transition-all shadow-lg"
        >
          <FiPlus size={16} /> New Post
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
          <p className="text-gray-500 mb-4">No blog posts yet. Create your first article!</p>
          <button
            onClick={handleNewPost}
            className="inline-flex items-center gap-2 bg-brand-gold text-white px-6 py-3 rounded-xl text-sm font-bold"
          >
            <FiPlus size={16} /> Create First Post
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map(post => (
            <div key={post.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden group hover:shadow-md transition-all">
              {post.cover_image && (
                <div className="h-40 overflow-hidden">
                  <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                    post.published ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {post.published ? 'Published' : 'Draft'}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(post.created_at).toLocaleDateString()}
                  </span>
                  {post.gallery_images && post.gallery_images.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-500 flex items-center gap-1">
                      <FiImage size={10} /> {post.gallery_images.length}
                    </span>
                  )}
                </div>
                <h3 className="font-serif font-bold text-gray-800 mb-2 line-clamp-2">{post.title}</h3>
                <p className="text-sm text-gray-500 line-clamp-2 mb-4">{post.excerpt || 'No excerpt'}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleEditPost(post)} className="flex items-center gap-1 text-xs font-semibold text-brand-darkSage hover:text-brand-gold transition-colors bg-gray-50 px-3 py-2 rounded-lg">
                    <FiEdit2 size={12} /> Edit
                  </button>
                  {post.published && (
                    <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs font-semibold text-brand-gold hover:text-brand-darkSage transition-colors bg-gray-50 px-3 py-2 rounded-lg">
                      <FiEye size={12} /> View
                    </a>
                  )}
                  <button onClick={() => handleDelete(post.id)} className="flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-600 transition-colors bg-gray-50 px-3 py-2 rounded-lg ml-auto">
                    <FiTrash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ToolbarLabel = ({ text }) => (
  <span className="text-[9px] font-black uppercase tracking-[0.15em] text-gray-300 mr-1 select-none">{text}</span>
);

const ToolbarBtn = ({ label, icon, onClick, bold, italic, underline, strike, color, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title || label}
    className={`px-2.5 py-1.5 text-[11px] rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all flex items-center gap-1 ${
      bold ? 'font-bold' : ''
    } ${italic ? 'italic' : ''} ${underline ? 'underline' : ''} ${strike ? 'line-through' : ''}`}
    style={color ? { color } : {}}
  >
    {icon || label}
  </button>
);

export default AdminBlog;